import React from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import en from '../language/Eng.json';
import TopMenu from './menu/Top_Menu';
import Bottom from './menu/Bottom';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Inkfree';
    src: url('/font/Inkfree.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'AmaticSC-Bold';
    src: url('/font/AmaticSC-Bold.ttf') format('truetype');
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }

  body, html {
    margin: 0;
    padding: 0;
    width: 100%;
    overflow-x: hidden;
  }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  background-color: #F4F1E6;
  min-height: 100vh;
  padding: 120px 40px 150px; 
  color: #9e5e04;
  font-family: 'Inkfree', sans-serif;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  &, & * {
    font-family: 'Inkfree', sans-serif;
  }
`;

const PageContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  flex: 1;
  width: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 60px;
  opacity: 0;
  animation: ${slideUp} 0.6s ease forwards;
  animation-delay: 0.1s;
`;

const HeaderTitle = styled.h1`
  font-size: 1rem;
  font-weight: normal;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0; 
`;

const ImageMenuButton = styled.a`
  font-size: 0.8rem;
  color: #9e5e04;
  text-decoration: none;
  padding: 6px 15px;
  border: 1px solid #9e5e04;
  border-radius: 4px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  cursor: pointer;
  background-color: transparent;

  &:hover {
    background-color: #9e5e04;
    color: #F4F1E6;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px 80px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 50px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
  opacity: 0;
  animation: ${slideUp} 0.8s ease forwards;

  &:nth-child(1) { animation-delay: 0.2s; }
  &:nth-child(2) { animation-delay: 0.4s; }
  &:nth-child(3) { animation-delay: 0.6s; }

  @media (max-width: 768px) {
    display: contents;
  }
`;

const SectionContainer = styled.div<{ mobileOrder?: number }>`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    order: ${props => props.mobileOrder || 3};
    opacity: 0;
    animation: ${slideUp} 0.8s ease forwards;
    animation-delay: 0.2s;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'AmaticSC-Bold', cursive;
  font-size: 1.8rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
`;

const ItemContainer = styled.div`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-size: 0.85rem;
  font-weight: bold;
`;

const ItemName = styled.span`
  flex: 1;
  padding-right: 15px;
`;
const ItemPrice = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
`;

const ItemDescription = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.4;
  white-space: pre-wrap;
  opacity: 0.8;
`;

const Note = styled.p`
  font-size: 0.8rem;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-top: 10px;
  opacity: 0.9;
`;

const getMobileOrder = (title: string) => {
  const foodTitles = ['SNACK', 'ENTRÉE', 'MAINS', 'SIDES', 'DESSERTS'];
  const nonAlcoholicTitles = ['COFFEE HOT R/L', 'COFFEE ICED', 'LOOSE LEAF TEA', 'OTHERS', 'NON-ALCOHOLIC'];
  
  if (foodTitles.includes(title)) return 1;
  if (nonAlcoholicTitles.includes(title)) return 2;
  return 3;
};

const Menu: React.FC = () => {
  const data = en.menuPage;

  return (
    <>
      <GlobalStyle />
      <TopMenu />
      <Container>
        <PageContent>

          <HeaderContainer>
            <HeaderTitle>{data.title}</HeaderTitle>
            <ImageMenuButton
              href="https://vuzagroup.direct.quickconnect.to/Joie_menu/index.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              VIEW MENU IMAGE
            </ImageMenuButton>
          </HeaderContainer>

          <Grid>
            {data.columns.map((column, colIdx) => (
              <Column key={colIdx}>
                {column.map((section, secIdx) => (
                  <SectionContainer key={secIdx} mobileOrder={getMobileOrder(section.title)}>
                    <SectionTitle>{section.title}</SectionTitle>
                    {section.items.map((item, itemIdx) => (
                      <ItemContainer key={itemIdx}>
                        <ItemHeader>
                          <ItemName>{item.name}</ItemName>
                          <ItemPrice>{item.price}</ItemPrice>
                        </ItemHeader>
                        {item.description && <ItemDescription>{item.description}</ItemDescription>}
                      </ItemContainer>
                    ))}
                    {section.note && <Note>{section.note}</Note>}
                  </SectionContainer>
                ))}
              </Column>
            ))}
          </Grid>
        </PageContent>
      </Container>
      <Bottom />
    </>
  );
};

export default Menu;
